import type * as React from 'react'
import { useEffect, useState } from 'react'
import { hasSeenVideo, markVideoSeen } from '@/lib/audition/apply-flags'
import type { AuditionVideo } from '@/lib/audition/intro-video'
import { cn } from '@/lib/utils'
import { IntroVideo } from './intro-video'

/**
 * A wizard video plus its caption and its own seen-state.
 *
 * Seen-state is per video id, so watching the welcome video does not
 * pre-collapse the mid-flow one.
 *
 * The `key` is the whole mechanism. `IntroVideo`'s collapse effect only
 * ever syncs `defaultCollapsed` false→true, so a `defaultCollapsed` that
 * changed under a mounted player would yank it shut — which is exactly what
 * would happen here, because this component marks the video seen and would
 * then re-read `true` on its next render. Remounting per video id instead
 * means `defaultCollapsed` is captured once and is constant for the life of
 * the mount, so nothing ever fights the applicant's own Show/Hide.
 */
export function StepVideo({
  video,
  className,
  rememberSeen = true,
}: {
  video: AuditionVideo
  className?: string
  /**
   * Opt out of seen-state entirely: always expanded, nothing written. The
   * welcome screen passes false, because its own phase flag already decides
   * whether that screen appears at all — and while it IS appearing, the
   * video is the reason it exists, so it must never render collapsed.
   */
  rememberSeen?: boolean
}) {
  if (!rememberSeen) {
    return (
      <Captioned video={video} className={className}>
        <IntroVideo source={video.source} />
      </Captioned>
    )
  }
  return <SeenOnceVideo key={video.id} video={video} className={className} />
}

function Captioned({
  video,
  className,
  children,
}: {
  video: AuditionVideo
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {children}
      <p className="text-muted-foreground text-sm">{video.caption}</p>
    </div>
  )
}

function SeenOnceVideo({
  video,
  className,
}: {
  video: AuditionVideo
  className?: string
}) {
  // Read once, at mount, before the effect below writes it.
  const [seenBefore] = useState(() => hasSeenVideo(video.id))

  useEffect(() => {
    // Marked on mount, not on play: playback inside a YouTube iframe is not
    // observable from here, and the only promise this state makes is "we
    // have already put this in front of you once", which is true either way.
    markVideoSeen(video.id)
  }, [video.id])

  return (
    <Captioned video={video} className={className}>
      <IntroVideo source={video.source} defaultCollapsed={seenBefore} />
    </Captioned>
  )
}
